import type {MouseEvent} from 'react'
import {Link} from 'react-router-dom'
import type {LucideIcon} from 'lucide-react'
import {EntityType, type EntityLink as EntityLinkValue} from '@/types/entity-link'
import {entityDetailRoute} from '@/lib/routes'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Badge} from '@/components/ui/badge'
import {cn} from '@/lib/utils'

type EntityLinkSize = 'xs' | 'sm' | 'base'

interface EntityLinkProps {
    readonly value: EntityLinkValue | null | undefined
    readonly testId?: string
    readonly size?: EntityLinkSize
    readonly showIcon?: boolean
    readonly showBadge?: boolean
    readonly stopPropagation?: boolean
}

/* eslint-disable i18next/no-literal-string -- CSS class constants + entity-type → icon registry */
const sizeClasses: Record<EntityLinkSize, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
}

const iconSizeClasses: Record<EntityLinkSize, string> = {
    xs: 'h-3 w-3',
    sm: 'h-3.5 w-3.5',
    base: 'h-4 w-4',
}

const ENTITY_ICON: Readonly<Record<EntityType, LucideIcon>> = {
    [EntityType.IDENTITY]: CONCEPT_ICON.identity,
    [EntityType.PARTY]: CONCEPT_ICON.party,
    [EntityType.RESOURCE]: CONCEPT_ICON.resource,
    [EntityType.SYSTEM]: CONCEPT_ICON.system,
    [EntityType.ENFORCEMENT_OBJECT]: CONCEPT_ICON.enforcementObject,
    [EntityType.ACCESS_PROFILE]: CONCEPT_ICON.accessProfile,
    [EntityType.FUNCTION_GROUP]: CONCEPT_ICON['function-group'],
    [EntityType.AUTH_USER]: CONCEPT_ICON.authUser,
    [EntityType.REPORT_CATEGORY]: CONCEPT_ICON.reportCategory,
    [EntityType.REPORT_EXECUTION]: CONCEPT_ICON.executionDetail,
    [EntityType.CONNECTOR]: CONCEPT_ICON.connectorOutbound,
}
/* eslint-enable i18next/no-literal-string */

/**
 * Renders an `EntityLink` value (id + displayName + entityType + subType) as a
 * clickable chip — icon for `entityType`, label for `displayName`, optional
 * badge for `subType`. Single source of truth for cross-module entity-reference
 * rendering. The link target comes from `entityDetailRoute(entityType, id)`.
 *
 * When no detail route exists for the entity type, the chip renders as plain
 * text (icon + label + badge) without a link.
 */
export function EntityLink({
    value,
    testId,
    size = 'sm',
    showIcon = true,
    showBadge = true,
    stopPropagation,
}: EntityLinkProps) {
    if (!value) return null

    const Icon = ENTITY_ICON[value.entityType]
    const url = entityDetailRoute(value.entityType, value.id)

    const handleClick = (e: MouseEvent) => {
        if (stopPropagation) e.stopPropagation()
    }

    const content = (
        <>
            {showIcon ? (
                <Icon
                    className={cn(iconSizeClasses[size], 'shrink-0 opacity-70')}
                    data-testid={testId ? `${testId}-icon` : undefined}
                    aria-hidden="true"
                />
            ) : null}
            <span className="truncate" data-testid={testId ? `${testId}-label` : undefined}>
                {value.displayName}
            </span>
            {value.code ? (
                <span
                    className="ml-1 font-mono text-[11px] text-muted-foreground"
                    data-testid={testId ? `${testId}-code` : undefined}
                >
                    {value.code}
                </span>
            ) : null}
            {showBadge && value.subType ? (
                <Badge
                    variant="outline"
                    className="ml-1 px-1.5 py-0 text-[10px] font-normal uppercase"
                    data-testid={testId ? `${testId}-badge` : undefined}
                >
                    {value.subType}
                </Badge>
            ) : null}
        </>
    )

    const classes = cn(
        'inline-flex items-center gap-1.5 max-w-full',
        sizeClasses[size],
    )

    if (!url) {
        return (
            <span className={classes} data-testid={testId}>
                {content}
            </span>
        )
    }

    return (
        <Link
            to={url}
            data-testid={testId}
            onClick={handleClick}
            className={cn(
                classes,
                'text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none rounded-sm',
            )}
        >
            {content}
        </Link>
    )
}
